import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import {
    PciDashboardCounty,
    PciDashboardProviderStatusEnum,
    PciDashboardScenarioTypeEnum,
    PciDashboardSpecialty,
    PciDashboardStatusEnum
} from "@app/projects/network-assessment/pci-dashboard/shared";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersPayload";
import PciDashboardService from "@app/projects/network-assessment/pci-dashboard/shared/pciDashboardService";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { NewPatientClaimsEnum, TopCondition, TopProcedure } from "@app/projects/servicing-provider";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { groupBy, isEqual } from "lodash";
import React, { createContext, FC, useContext, useEffect, useState } from "react";

export interface PciDashboardFiltersContextValues {
  compareTimeslices: boolean;
  counties: PciDashboardCounty[];
  countyStatuses: DropdownItem<RegionSetStatusType>[];
  displayedFilters: PciDashboardFiltersPayload;
  filtersLoading: boolean;
  getFiltersPayload: () => PciDashboardFiltersPayload;
  newPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[];
  onClearFiltersClick: (showLineOfBusiness: boolean) => void;
  projectRunConfiguration: ProjectRunConfiguration;
  scenarioTypes: PciDashboardScenarioTypeEnum[];
  selectedCompareScenario: PciDashboardScenarioTypeEnum;
  selectedCounties: string[];
  selectedCountyStatuses: DropdownItem<RegionSetStatusType>[];
  selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum;
  selectedNewPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[];
  selectedScenario: PciDashboardScenarioTypeEnum;
  selectedSpecialties: DropdownItem<PciDashboardSpecialty>[];
  selectedTopConditions: DropdownItem<TopCondition>[];
  selectedTopProcedures: DropdownItem<TopProcedure>[];
  serviceAreas: NestingParentDropdownItem[];
  setCompareTimeslices: (compareTimeslices: boolean) => void;
  setDisplayedFilters: (displayedFilters: PciDashboardFiltersPayload) => void;
  setSelectedCompareScenario: (selectedCompareScenario: PciDashboardScenarioTypeEnum) => void;
  setSelectedCounties: (selectedCounties: string[]) => void;
  setSelectedCountyStatuses: (selectedCountyStatuses: DropdownItem<RegionSetStatusType>[]) => void;
  setSelectedLineOfBusiness: (selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum) => void;
  setSelectedNewPatientClaimsFilters: (selectedNewPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[]) => void;
  setSelectedSpecialties: (selectedSpecialties: DropdownItem<PciDashboardSpecialty>[]) => void;
  setSelectedScenario: (selectedScenario: PciDashboardScenarioTypeEnum) => void;
  setSelectedTopConditions: (selectedTopConditions: DropdownItem<TopCondition>[]) => void;
  setSelectedTopProcedures: (selectedTopProcedures: DropdownItem<TopProcedure>[]) => void;
  specialties: DropdownItem<PciDashboardSpecialty>[];
  topConditions: DropdownItem<TopCondition>[];
  topProcedures: DropdownItem<TopProcedure>[];
}

const initialValues: PciDashboardFiltersContextValues = {
  compareTimeslices: false,
  counties: [],
  countyStatuses: [],
  displayedFilters: null,
  filtersLoading: false,
  getFiltersPayload: () => null,
  newPatientClaimsFilters: [],
  onClearFiltersClick: () => null,
  projectRunConfiguration: null,
  scenarioTypes: [],
  selectedCompareScenario: null,
  selectedCounties: [],
  selectedCountyStatuses: [],
  selectedLineOfBusiness: null,
  selectedNewPatientClaimsFilters: [],
  selectedScenario: null,
  selectedSpecialties: [],
  selectedTopConditions: [],
  selectedTopProcedures: [],
  serviceAreas: [],
  setCompareTimeslices: () => null,
  setDisplayedFilters: () => null,
  setSelectedCompareScenario: () => null,
  setSelectedCounties: () => null,
  setSelectedCountyStatuses: () => null,
  setSelectedLineOfBusiness: () => null,
  setSelectedNewPatientClaimsFilters: () => null,
  setSelectedScenario: () => null,
  setSelectedSpecialties: () => null,
  setSelectedTopConditions: () => null,
  setSelectedTopProcedures: () => null,
  specialties: [],
  topConditions: [],
  topProcedures: []
}

export const PciDashboardFiltersContext = createContext(initialValues);

const PciDashboardFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [compareTimeslices, setCompareTimeslices] = useState<boolean>(initialValues.compareTimeslices);
  const [counties, setCounties] = useState<PciDashboardCounty[]>(initialValues.counties);
  const [newPatientClaimsFilters, setNewPatientClaimsFilters] = useState<DropdownItem<NewPatientClaimsEnum>[]>([]);
  const [countyStatuses, setCountyStatuses] = useState<DropdownItem<RegionSetStatusType>[]>([]);
  const [displayedFilters, setDisplayedFilters] = useState<PciDashboardFiltersPayload>(initialValues.displayedFilters);
  const [filtersLoading, setFiltersLoading] = useState<boolean>(initialValues.filtersLoading);
  const [projectRunConfiguration, setProjectRunConfiguration] = useState<ProjectRunConfiguration>(initialValues.projectRunConfiguration);
  const [scenarioTypes, setScenarioTypes] = useState<PciDashboardScenarioTypeEnum[]>([]);
  const [selectedCompareScenario, setSelectedCompareScenario] = useState<PciDashboardScenarioTypeEnum>(initialValues.selectedCompareScenario);
  const [selectedCounties, setSelectedCounties] = useState<string[]>(initialValues.selectedCounties);
  const [selectedCountyStatuses, setSelectedCountyStatuses] = useState<DropdownItem<RegionSetStatusType>[]>([]);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum>(initialValues.selectedLineOfBusiness);
  const [selectedNewPatientClaimsFilters, setSelectedNewPatientClaimsFilters] = useState<DropdownItem<NewPatientClaimsEnum>[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<PciDashboardScenarioTypeEnum>(initialValues.selectedScenario);
  const [selectedSpecialties, setSelectedSpecialties] = useState<DropdownItem<PciDashboardSpecialty>[]>(initialValues.selectedSpecialties);
  const [serviceAreas, setServiceAreas] = useState<NestingParentDropdownItem[]>(initialValues.serviceAreas);
  const [specialties, setSpecialties] = useState<DropdownItem<PciDashboardSpecialty>[]>(initialValues.specialties);
  const [topConditions, setTopConditions] = useState<DropdownItem<TopCondition>[]>(initialValues.topConditions);
  const [selectedTopConditions, setSelectedTopConditions] = useState<DropdownItem<TopCondition>[]>([]);
  const [topProcedures, setTopProcedures] = useState<DropdownItem<TopProcedure>[]>(initialValues.topProcedures);
  const [selectedTopProcedures, setSelectedTopProcedures] = useState<DropdownItem<TopProcedure>[]>(initialValues.topProcedures);
  const [error, setError] = useState("");

  const pciDashboardService = new PciDashboardService();
  const projectContext = useContext(CountyProjectContext);

  useEffect(() => {
    if (!!projectContext?.project?.projectId &&
      !!projectRunConfiguration &&
      projectContext?.runConfiguration?.pciDashboardStatus === PciDashboardStatusEnum.PublishJobFinished
    ) {
      if (!!displayedFilters?.lineOfBusiness) {
        return;
      }

      setFiltersLoading(true);
      pciDashboardService.getPciFilterData(projectContext.project.projectId, projectRunConfiguration.projectRunConfigurationId).then((data) => {
        if (!isEqual(data.specialties.map(x => x.specialtyGroupCode), specialties.map(x => x.key.specialtyGroupCode))) {
          const mappedSpecialties: DropdownItem<PciDashboardSpecialty>[] = data.specialties.map(x => {
            return { key: x, name: `${x.specialtyGroupCode} - ${x.specialtyGroupName}` }
          });

          setSpecialties(mappedSpecialties);
          setSelectedSpecialties(mappedSpecialties);
        }

        if (!isEqual(data.topProcedures.map(x => x.code), topProcedures.map(x => x.key.code))) {
          const mappedTopProcedures: DropdownItem<TopProcedure>[] = data.topProcedures.map(x => {
            return { key: x, name: `${x.code} - ${x.description}` }
          });
          setTopProcedures(mappedTopProcedures);
        }

        if (!isEqual(data.topConditions.map(x => x.code), topConditions.map(x => x.key.code))) {
          const mappedTopConditions: DropdownItem<TopCondition>[] = data.topConditions.map(x => {
            return { key: x, name: `${x.code} - ${x.description}` }
          });
          setTopConditions(mappedTopConditions);
        }

        if (!isEqual(data.countyStatuses, countyStatuses)) {
          const mappedCountyStatuses: DropdownItem<RegionSetStatusType>[] = data.countyStatuses.map(x => {
            return { key: x, name: x }
          });

          // on load, select all county statuses except opportunity
          setCountyStatuses(mappedCountyStatuses);
          setSelectedCountyStatuses(mappedCountyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity));
        }

        if (!isEqual(data.counties, counties)) {
          const groupedCountiesByState = groupBy(data.counties, x => x.stateName);
          const nestedServiceAreas: NestingParentDropdownItem[] = []

          for (const [key, value] of Object.entries(groupedCountiesByState)) {
            const children: DropdownItem<string>[] = value.map(x => {
              return { key: x.countySSA, name: x.countyName }
            });

            const nestedServiceArea: NestingParentDropdownItem = {
              checkboxStatus: CheckboxStateEnum.Checked,
              children,
              key,
              name: key,
            };

            nestedServiceAreas.push(nestedServiceArea)
          }

          setServiceAreas(nestedServiceAreas);
          setSelectedCounties(data.counties.map(x => x.countySSA));
          setCounties(data.counties);
        }

        if (!isEqual(data.scenarioTypes, scenarioTypes)) {
          setScenarioTypes(data.scenarioTypes);
        }

        if (!isEqual(data.newPatientFilters, newPatientClaimsFilters)) {
          const mappedNewPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[] = data.newPatientFilters.map(x => {
            return { key: x, name: x }
          });

          setNewPatientClaimsFilters(mappedNewPatientClaimsFilters);
          setSelectedNewPatientClaimsFilters(mappedNewPatientClaimsFilters);
        }

        setFiltersLoading(false);
      }, (error) => {
        setFiltersLoading(false);
        setError(error);
      });
    }
  }, [projectContext?.project, projectRunConfiguration]);

  useEffect(() => {
    const runConfiguration: ProjectRunConfiguration = projectContext.project.projectRunConfigurations.find(x =>
      x && projectContext.runConfiguration && x.projectRunConfigurationType === projectContext.runConfiguration.projectRunConfigurationType &&
      x.isLatestPublished &&
      !x.isProjected &&
      !x.isTermed &&
      !x.isProjectedTermed &&
      x.projectRunConfigurationId !== projectRunConfiguration?.projectRunConfigurationId
    );

    if (!!runConfiguration) {
      setProjectRunConfiguration(runConfiguration);
    }
  }, [projectContext.runConfiguration, projectContext.project]);

  useEffect(() => {
    // once the specialties and service areas have been determined, set those as being the displayed filters
    if (!filtersLoading && !!projectRunConfiguration) {
      const filters: PciDashboardFiltersPayload = getFiltersPayload();
      setDisplayedFilters(filters);
    }
  }, [filtersLoading, projectRunConfiguration]);

  useEffect(() => {
    if (!!error) {
      throw error;
    }
  }, [error]);

  const getFiltersPayload = (): PciDashboardFiltersPayload => {
    const filters: PciDashboardFiltersPayload = {
      claimsLabel: !!displayedFilters?.claimsLabel ? displayedFilters.claimsLabel : null,
      compareScenario: selectedCompareScenario,
      compareTimeslices: compareTimeslices,
      countySSAs: selectedCounties,
      countyStatuses: selectedCountyStatuses.map(x => x.key),
      insightsCategory: !!displayedFilters?.insightsCategory ? displayedFilters.insightsCategory : null,
      lineOfBusiness: !!selectedLineOfBusiness ? selectedLineOfBusiness : null,
      newPatientClaims: selectedNewPatientClaimsFilters.map(x => x.key),
      projectRunConfigurationType: projectRunConfiguration.projectRunConfigurationType,
      providerNetworkType: !!displayedFilters?.providerNetworkType ? displayedFilters.providerNetworkType : null,
      providerStatus: PciDashboardProviderStatusEnum.Standard,
      scenario: selectedScenario,
      sortDirection: null,
      sortedColumn: null,
      specialtyGroupCodes: selectedSpecialties.map(x => x.key.specialtyGroupCode),
      topConditions: selectedTopConditions.map(x => x.key.code),
      topProcedures: selectedTopProcedures.map(x => x.key.code)
    }

    return filters;
  }

  /**
   * Clears all filters and restores defaults.
   * @param showLineOfBusiness - if true, the line of business filter will be set to Total
   */
  const onClearFiltersClick = (showLineOfBusiness: boolean): void => {
    // if defaults are restored, all available counties and states should be displayed, selected
    setSelectedCounties(counties.map(x => x.countySSA));

    if (!!countyStatuses.length) {
      setSelectedCountyStatuses(countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity));
    }

    if (!!newPatientClaimsFilters.length) {
      setSelectedNewPatientClaimsFilters(newPatientClaimsFilters);
    }

    setSelectedSpecialties(specialties);
    setCompareTimeslices(false);
    setSelectedScenario(null);
    setSelectedCompareScenario(null);
    setSelectedTopProcedures([]);
    setSelectedTopConditions([]);

    if (showLineOfBusiness) {
      setSelectedLineOfBusiness(ClaimsActivityLineOfBusinessTypeEnum.Total);
    }
  }

  return (
    <PciDashboardFiltersContext.Provider
      value={{
        compareTimeslices,
        counties,
        countyStatuses,
        displayedFilters,
        filtersLoading,
        getFiltersPayload,
        newPatientClaimsFilters,
        onClearFiltersClick,
        projectRunConfiguration,
        scenarioTypes,
        selectedCompareScenario,
        selectedCounties,
        selectedCountyStatuses,
        selectedLineOfBusiness,
        selectedNewPatientClaimsFilters,
        selectedScenario,
        selectedSpecialties,
        selectedTopConditions,
        selectedTopProcedures,
        serviceAreas,
        setCompareTimeslices,
        setDisplayedFilters,
        setSelectedCompareScenario,
        setSelectedCounties,
        setSelectedCountyStatuses,
        setSelectedLineOfBusiness,
        setSelectedNewPatientClaimsFilters,
        setSelectedScenario,
        setSelectedSpecialties,
        setSelectedTopConditions,
        setSelectedTopProcedures,
        specialties,
        topConditions,
        topProcedures
      }}>
      {props.children}
    </PciDashboardFiltersContext.Provider>
  )
}

export default PciDashboardFiltersContextProvider;